import {useEffect, useRef} from 'react';
import {BRAND} from './brand';
import {DesktopProvider, useDesktop} from './desktop/DesktopContext';
import {DesktopDock} from './desktop/DesktopDock';
import {MenuBar} from './desktop/MenuBar';
import {Wallpaper} from './desktop/Wallpaper';
import {Window} from './desktop/Window';
import {MobileShell} from './mobile/MobileShell';
import {AppLinkProvider} from './shell/AppLink';
import {StoreProvider} from './store/StoreContext';
import {I18nProvider} from './i18n';
import {ToastProvider} from './ui/Toast';

function Desktop() {
  const {windows} = useDesktop();

  return (
    <div className="relative h-dvh w-screen overflow-hidden">
      <Wallpaper />
      <MenuBar />

      <main className="absolute inset-0 pt-9">
        {windows.map((state) => (
          <Window key={state.id} state={state} />
        ))}
      </main>

      <DesktopDock />
    </div>
  );
}

function Shell() {
  const {openApp, shellMode} = useDesktop();
  const bootedRef = useRef(false);

  // The desktop opens on the market, because a marketplace with nothing on
  // screen is a marketplace nobody browses. A phone opens on the home screen
  // instead: covering it with a full-screen app would hide every other app
  // behind a gesture the user has not learned yet.
  useEffect(() => {
    if (shellMode !== 'desktop' || bootedRef.current) return;
    bootedRef.current = true;
    openApp(BRAND.bootAppId);
  }, [openApp, shellMode]);

  return shellMode === 'mobile' ? <MobileShell title={BRAND.name} /> : <Desktop />;
}

/**
 * Provider order is a dependency order, not a preference:
 * toasts exist before the store (the store reports failures through them), the
 * store before the shell (badges read it), and app-to-app links last, because
 * they need a shell that already knows how to open a window.
 */
export default function App() {
  return (
    <I18nProvider>
      <ToastProvider>
        <StoreProvider>
          <DesktopProvider>
            <AppLinkProvider>
              <Shell />
            </AppLinkProvider>
          </DesktopProvider>
        </StoreProvider>
      </ToastProvider>
    </I18nProvider>
  );
}
